import { useWeb3React } from "@web3-react/core";
import { getAnalytics, logEvent } from "firebase/analytics";
import { useSnackbar } from "notistack";
import { FC } from "react";

import { MetamaskSVG, WalletConnectSVG, XDEFISVG } from "../../../assets";
import {
  Button,
  IOption,
  Options,
  SnackMessage,
  Text,
  WalletOption,
} from "../../../components";
import { metamask } from "../../../connectors/metaMask";
import { walletConnect } from "../../../connectors/walletConnect";
import { useNetworks, useSupportedWalletsMap } from "../../../hooks";
import useNameResolver from "../../../hooks/utils/useNameResolver";
import { WalletItem } from "../../../interfaces";
import { shortAddress } from "../../../utils/utils";
import { useWeb3Connectors } from "../../Web3ConnectorsProvider";

interface Props {
  setSelectedEvmWallet: (name: string | null) => void;
  onCloseClick: () => void;
}

export const EvmSection: FC<Props> = ({
  setSelectedEvmWallet,
  onCloseClick,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    networks: { evm },
  } = useNetworks();
  const { name: evmName } = useNameResolver(evm.chainId, evm.address);
  const { connector } = useWeb3React();
  const { connectorsMap } = useWeb3Connectors();
  const analytics = getAnalytics();
  const supportedWalletsMap = useSupportedWalletsMap();

  const onMetamaskClick = () => {
    metamask
      .activate()
      .then(() => {
        localStorage.setItem(
          "lastEvmConnector",
          supportedWalletsMap.evm.metamask.id,
        );
        setSelectedEvmWallet(supportedWalletsMap.evm.metamask.id);
        onCloseClick();
        logEvent(analytics, "wallet_connected_success", { name: "metamask" });
      })
      .catch((err) => {
        enqueueSnackbar(
          <SnackMessage
            message={{
              text: `There was a problem while connecting to metamask. Error: ${
                err?.message || "unknown"
              }`,
              variant: "error",
            }}
          />,
        );
        onCloseClick();
        logEvent(analytics, "wallet_connected_fail", { name: "metamask" });
      });
  };

  // const onCoinbaseWalletClick = () => {
  //   coinbase
  //     .activate()
  //     .then(() => {
  //       localStorage.setItem(
  //         "lastEvmConnector",
  //         supportedWalletsMap.evm.coinbase.id,
  //       );
  //       setSelectedEvmWallet(supportedWalletsMap.evm.coinbase.id);
  //       onCloseClick();
  //       logEvent(analytics, "wallet_connected_success", { name: "coinbase" });
  //     })
  //     .catch((err) => {
  //       enqueueSnackbar(
  //         <SnackMessage
  //           message={{
  //             text: `There was a problem while connecting to coinbase wallet. Error: ${
  //               err?.message || "unknown"
  //             }`,
  //             variant: "error",
  //           }}
  //         />,
  //       );
  //       onCloseClick();
  //       logEvent(analytics, "wallet_connected_fail", { name: "coinbase" });
  //     });
  // };

  const onWalletConnectClick = () => {
    // WalletConnect renders within the iframe and we couldn't find a way to increase its z-index above the modal.
    onCloseClick();

    walletConnect
      .activate()
      .then(() => {
        localStorage.setItem(
          "lastEvmConnector",
          supportedWalletsMap.evm.walletConnect.id,
        );
        setSelectedEvmWallet(supportedWalletsMap.evm.walletConnect.id);
        onCloseClick();
        logEvent(analytics, "wallet_connected_success", {
          name: "wallet_connect",
        });
      })
      .catch((err) => {
        enqueueSnackbar(
          <SnackMessage
            message={{
              text: `There was a problem while connecting to walletConnect wallet. Error: ${
                err?.message || "unknown"
              }`,
              variant: "error",
            }}
          />,
        );
        onCloseClick();
        logEvent(analytics, "wallet_connected_fail", {
          name: "wallet_connect",
        });
      });
  };

  const onXDEFIWalletClick = () => {
    if (!connectorsMap.xdefi) {
      onCloseClick();
      enqueueSnackbar(
        <SnackMessage
          message={{
            text: `You need to install xdefi wallet extension first.`,
            variant: "error",
          }}
        />,
      );
      return;
    }
    connectorsMap.xdefi
      .activate()
      ?.then(() => {
        localStorage.setItem(
          "lastEvmConnector",
          supportedWalletsMap.evm.xdefi.id,
        );
        setSelectedEvmWallet(supportedWalletsMap.evm.xdefi.id);
        onCloseClick();
        logEvent(analytics, "wallet_connected_success", {
          name: "xdefi",
        });
      })
      .catch((err) => {
        enqueueSnackbar(
          <SnackMessage
            message={{
              text: `There was a problem while connecting to xdefi wallet. Error: ${
                err?.message || "unknown"
              }`,
              variant: "error",
            }}
          />,
        );
        onCloseClick();
        logEvent(analytics, "wallet_connected_fail", {
          name: "xdefi",
        });
      });
  };

  const disconnectEvmWallet = () => {
    if (connector?.deactivate) {
      connector.deactivate();
    } else {
      connector.resetState();
    }
    localStorage.removeItem("lastEvmConnector");
    setSelectedEvmWallet(null);
  };

  const evmWallets: Record<string, WalletItem> = {
    Metamask: {
      name: "Metamask",
      logo: (
        <MetamaskSVG
          className="connect-wallet-modal__svg"
          width={24}
          height={24}
        />
      ),
      onClick: onMetamaskClick,
      address: evm.address,
      disabled: false,
    },
    // "Coinbase Wallet": {
    //   name: "Coinbase Wallet",
    //   logo: (
    //     <CoinbaseWalletSVG
    //       className="connect-wallet-modal__svg"
    //       width={24}
    //       height={24}
    //     />
    //   ),
    //   onClick: onCoinbaseWalletClick,
    //   address: evm.address,
    //   disabled: false,
    // },
    "Wallet Connect": {
      name: "Wallet Connect",
      logo: (
        <WalletConnectSVG
          className="connect-wallet-modal__svg"
          width={24}
          height={24}
        />
      ),
      onClick: onWalletConnectClick,
      address: evm.address,
      disabled: false,
    },
    "XDEFI Wallet": {
      name: "XDEFI Wallet",
      logo: (
        <XDEFISVG
          className="connect-wallet-modal__svg"
          width={24}
          height={24}
        />
      ),
      onClick: onXDEFIWalletClick,
      address: evm.address,
      disabled: false,
    },
  };

  const evmWalletOptions: IOption[] = Object.values(evmWallets)
    .filter((wallet) => !wallet.address)
    .map((wallet) => {
      return {
        id: wallet.name,
        content: <WalletOption wallet={wallet} />,
      };
    });

  return evm.address ? (
    <div className="connect-wallet-modal__connection-info">
      <div className="connect-wallet-modal__connection-info-wrapper">
        <p className="connect-wallet-modal__connection-info-title">EVM</p>
        <Text
          text={`Connected Wallet: <span>${
            evmName ? evmName : shortAddress(evm.address)
          }</span> `}
        />
      </div>
      <div>
        <Button onClick={disconnectEvmWallet} variant="OUTLINED">
          Disconnect
        </Button>
      </div>
    </div>
  ) : (
    <div className="connect-wallet-modal__connection-info-options">
      <p className="connect-wallet-modal__connection-info-title">EVM</p>
      <Options
        id="EVM"
        handleChange={(name) => evmWallets[name].onClick()}
        options={evmWalletOptions}
      />
    </div>
  );
};
